import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section } from "../components/Section.js";

interface FaqItem {
  q: string;
  a: string;
}

/** Twelve buyer-facing Q&As. Order intentionally surfaces the questions
 *  operators ask in the first sales call (integration time, RTP, certs)
 *  before the back-office topics (settlement, disputes, jurisdictions). */
const FAQ_ITEMS: FaqItem[] = [
  {
    q: "How long does an integration take?",
    a: "Most operators wire the adapter in about a week — six REST endpoints and one WebSocket stream. The fastest pilot we ran shipped in three days. We pair-program with your engineer for the first round end-to-end, then leave you with the curl recipes and a sandbox key.",
  },
  {
    q: "What's the RTP and house edge?",
    a: "Vault Breaker (the flagship crash title) ships with 99.0% RTP — a 1% house edge implemented as P(crash ≥ c) = 99 / (100·c). Pressure Plate runs at 95% RTP. Both are unit-tested at 10k+ rounds and reproducible from disclosed seeds. The math is open-source under @vb/engine.",
  },
  {
    q: "Are you certified?",
    a: "Not yet. We're a new studio. Our public commitment is to commence GLI-19 (or iTechLabs equivalent) audit within 60 days of the first signed pilot, and to commission audits as each operator's regulator requires them. The strongest guarantee we ship today is per-round verifiability via the provably-fair seed reveal.",
  },
  {
    q: "How does settlement work?",
    a: "Weekly cycles. We track GGR per operator, settle in USDT or USDC on Solana, Ethereum, or Polygon, and post the on-chain transaction hash to your console. The operator wallet is the source of truth — Vault Breaker never custodies player funds.",
  },
  {
    q: "Can I change my payout wallet?",
    a: "Yes. Email hello@vaultbreaker.dev with the new address and chain. We require a small signed message from the new wallet to verify ownership before the rotation takes effect. Mid-cycle changes settle the previous balance to the old address, then switch.",
  },
  {
    q: "What happens if a player disputes a round?",
    a: "Every round is reproducible from three values: serverSeed, clientSeed, and nonce. Pull GET /rgs/round/:id/seeds, hand the player the verifier link, and they (or you) can re-derive the bust point. If we ever ship a buggy resolve, the rollback endpoint is idempotent — you re-issue with the same roundId and the wallet credit reverses cleanly.",
  },
  {
    q: "Which jurisdictions can I deploy in?",
    a: "We're a B2B engine — your jurisdiction strategy is yours. We've worked with operators in Curaçao, Costa Rica, Anjouan, Tobique, and Isle of Man. We do not solicit or ship to U.S., U.K., Australia, France, Spain, Italy, the Netherlands, or any FATF-blacklisted country until our certifications catch up to the local regulator.",
  },
  {
    q: "What player data do you collect?",
    a: "The minimum required to run the game: an opaque playerId (you mint it), session ID, and bet history bound to that session. No PII, no IP logging beyond Fly's edge defaults, no cookies on the iframe origin. The privacy policy at /legal/privacy is the canonical document.",
  },
  {
    q: "Who owns the game IP and engine?",
    a: "Vault Breaker Studios owns the games and the @vb/engine library. The engine is MIT-licensed and published on GitHub — anyone can read the math, fork the verifier, or audit the RNG. The hosted RGS, the art, the brand, and the operator integrations are proprietary.",
  },
  {
    q: "Can I self-host the engine?",
    a: "Technically yes — @vb/engine is MIT and runs on any Node 20+ runtime. In practice we recommend the hosted RGS because it includes the websocket clock, the seed-reveal endpoint, the rate limiter, and the settlement ledger — re-implementing those is the hard 80% of running a casino game. Self-host customers don't get the 10% revenue share — you'd pay a flat license instead. Talk to sales.",
  },
  {
    q: "What's the SLA?",
    a: "We target 99.5% monthly uptime on the game-server, measured externally. Live status is at /status. The current generation is single-region (Fly LHR) — we'll go multi-region once a paying operator's traffic justifies it. Incidents are emailed within 4 hours; post-mortems within 7 days.",
  },
  {
    q: "What happens if Vault Breaker shuts down?",
    a: "The engine is open-source and the round seeds are independently verifiable, so any pending player rounds can be re-derived and paid out from your operator wallet without our infrastructure. We commit to 90 days' notice and a documented self-host migration if we ever wind the studio down. Your settlement balance is a wallet-to-wallet transfer — there's no platform escrow to claw back.",
  },
];

function buildFaqLd(items: FaqItem[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: it.a,
      },
    })),
  };
}

export function FAQ() {
  const jsonLd = buildFaqLd(FAQ_ITEMS);
  return (
    <>
      <Seo
        title="FAQ · Vault Breaker Studios"
        description="Frequently asked questions about Vault Breaker Studios — integration, RTP, certifications, settlement, disputes, and jurisdictions."
        path="/faq"
        jsonLd={jsonLd}
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          FAQ
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(40px, 5.5vw, 64px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Frequently asked questions
        </h1>
        <p
          style={{
            fontSize: 17,
            color: "var(--fg-2)",
            marginTop: 14,
            maxWidth: 720,
            lineHeight: 1.55,
          }}
        >
          Twelve answers. The questions operators ask in the first call, in roughly the order
          they ask them.
        </p>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 880 }}>
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {FAQ_ITEMS.map((item, i) => (
            <FaqRow key={item.q} item={item} first={i === 0} />
          ))}
        </Card>
      </Section>

      <Section style={{ paddingTop: 0, maxWidth: 880 }}>
        <Card style={{ padding: 22, display: "grid", gap: 14 }}>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 24,
              color: "var(--fg-1)",
              textTransform: "uppercase",
              letterSpacing: "0.01em",
            }}
          >
            Don't see your question?
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Email{" "}
            <a href="mailto:hello@vaultbreaker.dev" style={{ color: "var(--cyan)" }}>
              hello@vaultbreaker.dev
            </a>{" "}
            and a human replies within one business day.
          </p>
          <Link to="/contact" style={{ textDecoration: "none", justifySelf: "start" }}>
            <Button variant="gold" style={{ padding: "12px 18px", fontSize: 14 }}>
              CONTACT SALES →
            </Button>
          </Link>
        </Card>
      </Section>
    </>
  );
}

function FaqRow({ item, first }: { item: FaqItem; first: boolean }) {
  return (
    <details
      className="vb-faq-row"
      style={{
        padding: "18px 22px",
        borderTop: first ? "none" : "1px solid var(--line)",
      }}
    >
      <summary
        style={{
          cursor: "pointer",
          fontSize: 15,
          color: "var(--fg-1)",
          fontFamily: "var(--font-ui)",
          fontWeight: 600,
          listStyle: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          lineHeight: 1.4,
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.color = "var(--gold-bright)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.color = "var(--fg-1)";
        }}
      >
        <span>{item.q}</span>
        <span
          aria-hidden
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 14,
            color: "var(--fg-3)",
            flexShrink: 0,
          }}
        >
          +
        </span>
      </summary>
      <p
        style={{
          margin: "12px 0 0 0",
          fontSize: 14,
          color: "var(--fg-2)",
          lineHeight: 1.7,
        }}
      >
        {item.a}
      </p>
    </details>
  );
}
